/**
 * Logic for computing which parts of the table UI are visible, including the
 * collapsed state of the table.
 */

import BenchlingConfig from 'BenchlingConfig';
import {
  STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {MINIMUM_ROWS_FOR_VIRTUALIZATION} from 'notebook/tables/constants/TableLayoutConstants';

import {CollapsedTableState} from '../../collapse/reduceCollapsedTablesUI';

/**
 * Whether the table footer should be rendered.
 *
 * Mirrors the legacy `TableEditable.shouldRenderFooter()`: the footer is hidden
 * when explicitly suppressed, in viewer mode, or in the single-input view.
 */
export function computeShowFooter(hideFooter: boolean, viewer: boolean, isSingleInputView: boolean): boolean {
  return !hideFooter && !viewer && !isSingleInputView;
}

/**
 * Whether the formula bar should be visible.
 *
 * Mirrors the legacy `TableEditable.isFormulaBoxVisible()`. The
 * `firstRowLength > 0` check guards against broken result tables in the wild
 * that have no columns and no computed table, which would otherwise crash when
 * clicking on their samples.
 */
export function computeShowFormulaBar(
  showFormulaBox: boolean,
  viewer: boolean,
  isCollapsed: boolean,
  firstRowLength: number
): boolean {
  return showFormulaBox && !viewer && !isCollapsed && firstRowLength > 0;
}

/**
 * Whether virtualization (windowed rendering via Virtuoso) is enabled for the
 * table. Virtualization is disabled in viewer mode (exports/print preview render
 * the full table) and for small tables below the row threshold, where the
 * JavaScript overhead of virtualization is not worth it.
 */
export function computeIsVirtualizationEnabled(viewer: boolean, rowCount: number): boolean {
  return !viewer && rowCount > MINIMUM_ROWS_FOR_VIRTUALIZATION;
}

/**
 * Whether the sticky header is both being tracked and actually displayed: the
 * table has been scrolled past its header, a positioning has been computed, and
 * there is an active multi-selection.
 */
export function computeIsStickyHeaderVisible(
  canShowStickyHeader: boolean,
  hasStickyHeaderPositioning: boolean,
  hasMultiSelection: boolean
): boolean {
  return canShowStickyHeader && hasStickyHeaderPositioning && hasMultiSelection;
}

export function createInputTableCollapseId(stageEntryId: string): string {
  return `input-${stageEntryId}`;
}

export function createOutputTableCollapseId(stageEntryId: string): string {
  return `output-${stageEntryId}`;
}

export function getCollapseId({collapseId, cid}: {collapseId?: string; cid: string}): string {
  return collapseId || cid;
}

/**
 * Whether a table kind is exempt from collapsing (and therefore from being
 * substituted by a CSV export in read-only views).
 *
 * TODO: This helper is temporary and should be removed once
 * `ENABLE_MIXTURE_PREP_ENTRY_EVALUATION` is fully rolled out, after which callers
 * can go back to checking `STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE` directly.
 * Until the flag is on, Mixture ingredients tables do not participate in CSV
 * export, so they must not collapse; once it is on they collapse like other large
 * tables. Mixture metadata tables are permanently exempt (see
 * `STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE`).
 */
export function isTableKindExemptFromCollapse(tableKind: StructuredTableKind | null): boolean {
  if (
    tableKind === StructuredTableKind.MIXTURE_INGREDIENTS &&
    !BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION
  ) {
    return true;
  }
  return STRUCTURED_TABLE_KINDS_THAT_DO_NOT_COLLAPSE.includes(tableKind);
}

export function getIsOverInlineRowLimit(numRows: number, tableKind: StructuredTableKind | null): boolean {
  const rowLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_ROW_LIMIT;
  return (
    !isTableKindExemptFromCollapse(tableKind) &&
    numRows > 0 &&
    Number.isInteger(rowLimit) &&
    rowLimit > -1 &&
    numRows > rowLimit
  );
}

export function getIsOverInlineCellLimit(numCells: number, tableKind: StructuredTableKind | null): boolean {
  const cellLimit = BenchlingConfig.ELN_INLINE_TABLE_VIEW_CELL_LIMIT;
  return (
    !isTableKindExemptFromCollapse(tableKind) &&
    numCells > 0 &&
    Number.isInteger(cellLimit) &&
    cellLimit > -1 &&
    numCells > cellLimit
  );
}

export function shouldCollapseTableInitially(
  numRows: number,
  numCells: number,
  tableKind: StructuredTableKind | null,
  autoCollapseLimit?: number
): boolean {
  return (
    (!isTableKindExemptFromCollapse(tableKind) &&
      numRows > (autoCollapseLimit || BenchlingConfig.ELN_ROW_LIMIT_FOR_AUTO_COLLAPSE)) ||
    getIsOverInlineRowLimit(numRows, tableKind) ||
    getIsOverInlineCellLimit(numCells, tableKind)
  );
}

export function isTableCollapsed({
  collapseId,
  collapsedStateById,
  rowCount,
  cellCount,
  tableKind,
  autoCollapseLimit,
}: {
  collapseId: string;
  collapsedStateById: CollapsedTableState;
  rowCount: number;
  cellCount: number;
  tableKind: StructuredTableKind | null;
  autoCollapseLimit?: number;
}): boolean {
  return (
    collapsedStateById[collapseId] ??
    shouldCollapseTableInitially(rowCount, cellCount, tableKind, autoCollapseLimit)
  );
}

/**
 * Whether the table is "perma-collapsed" — i.e. a large table that is exported as
 * CSV in a read-only view. This takes precedence over `isCollapsible`.
 */
export function computeIsPermaCollapsed({
  isOverInlineRowLimit,
  isOverInlineCellLimit,
  isReadonlyViewer,
  isSupportedByCsvExport,
}: {
  isOverInlineRowLimit: boolean;
  isOverInlineCellLimit: boolean;
  isReadonlyViewer: boolean;
  isSupportedByCsvExport: boolean;
}): boolean {
  if (isReadonlyViewer && !isSupportedByCsvExport) {
    return false;
  }
  if (isReadonlyViewer && (isOverInlineRowLimit || isOverInlineCellLimit)) {
    return true;
  }
  return false;
}

/**
 * Whether the table should be collapsed on its first render (before the user has
 * toggled it), based on its size and whether it is collapsible.
 */
export function computeIsCollapsedInitially(
  isCollapsible: boolean,
  rowCount: number,
  cellCount: number,
  tableKind: StructuredTableKind | null,
  autoCollapseLimit?: number
): boolean {
  return isCollapsible && shouldCollapseTableInitially(rowCount, cellCount, tableKind, autoCollapseLimit);
}

/**
 * The effective collapsed state of the table, combining forced/focused/maximized
 * modes, perma-collapse, collapsibility, viewer mode and the persisted redux
 * collapse state. Mirrors the legacy `useTableCollapse.getIsCollapsed()`.
 */
export function computeIsCollapsed({
  forceCollapsed,
  isFocused,
  isMaximized,
  isSplitScreen,
  isPermaCollapsed,
  isCollapsible,
  viewer,
  collapsedStateInRedux,
  isCollapsedInitially,
}: {
  forceCollapsed: boolean;
  isFocused: boolean;
  isMaximized: boolean;
  isSplitScreen: boolean;
  isPermaCollapsed: boolean;
  isCollapsible: boolean;
  viewer: boolean;
  collapsedStateInRedux: boolean | undefined;
  isCollapsedInitially: boolean;
}): boolean {
  if (forceCollapsed || isFocused) {
    return true;
  }

  if (isMaximized || isSplitScreen) {
    return false;
  }

  // Perma-collapse (a table that is exported as CSV in a read-only view) takes precedence
  // over `isCollapsible`. Group-contained tables (e.g. Mixture Prep ingredients tables) are
  // rendered with `isCollapsible: false`, but a large one must still collapse to its header +
  // CSV banner in the export/print PDF instead of rendering the full table.
  if (isPermaCollapsed) {
    return true;
  }

  if (!isCollapsible) {
    return false;
  }

  // Print and export view should render non-permacollapsed tables as expanded
  if (viewer) {
    return false;
  }

  return collapsedStateInRedux ?? isCollapsedInitially;
}

/**
 * Computes the full collapsed state model for a table from its dimensions and
 * layout-related flags. Mirrors the legacy `useTableCollapse` hook (minus the
 * redux read, which is now provided via `collapsedStateInRedux`).
 */
export function computeCollapseState({
  rowCount,
  cellCount,
  tableKind,
  isLegacyTable,
  isCollapsible,
  autoCollapseLimit,
  viewer,
  forceCollapsed,
  isFocused,
  isMaximized,
  isSplitScreen,
  collapsedStateInRedux,
}: {
  rowCount: number;
  cellCount: number;
  tableKind: StructuredTableKind | null;
  isLegacyTable: boolean;
  isCollapsible: boolean;
  autoCollapseLimit?: number;
  viewer: boolean;
  forceCollapsed: boolean;
  isFocused: boolean;
  isMaximized: boolean;
  isSplitScreen: boolean;
  collapsedStateInRedux: boolean | undefined;
}): {isCollapsed: boolean; isCollapsedInitially: boolean; isPermaCollapsed: boolean} {
  const isCollapsedInitially = computeIsCollapsedInitially(
    isCollapsible,
    rowCount,
    cellCount,
    tableKind,
    autoCollapseLimit
  );

  const isPermaCollapsed = computeIsPermaCollapsed({
    isOverInlineRowLimit: getIsOverInlineRowLimit(rowCount, tableKind),
    isOverInlineCellLimit: getIsOverInlineCellLimit(cellCount, tableKind),
    isReadonlyViewer: viewer,
    isSupportedByCsvExport: !isLegacyTable,
  });

  const isCollapsed = computeIsCollapsed({
    forceCollapsed,
    isFocused,
    isMaximized,
    isSplitScreen,
    isPermaCollapsed,
    isCollapsible,
    viewer,
    collapsedStateInRedux,
    isCollapsedInitially,
  });

  return {isCollapsed, isCollapsedInitially, isPermaCollapsed};
}
